import { useEffect, useMemo, useState } from 'react';
import Button from '../../../shared/components/Button';
import { formatPrice } from '../../../shared/utils/priceUtils';
import {
  useCalculatorPreview,
  usePersistSelection,
  useSalesAssumptions,
  useSalesFeatures,
} from '../hooks/useSales';
import {
  COMMERCIAL_MODEL_LABELS,
  PRICE_TIER_LABELS,
  SalesCommercialModel,
  SalesPriceTier,
  type SalesCalculatorResult,
  type SalesCalculatorSelection,
} from '../types';
import {
  buildQuotePayload,
  fractionToPercentInput,
  percentInputToFraction,
} from '../quote';

interface CalculatorPanelProps {
  canManage: boolean;
  /** Raised when a quote is frozen, so the page can offer "raise an invoice". */
  onSelectionSaved: (selection: SalesCalculatorSelection) => void;
}

/** A percentage input that stores the 0..1 fraction the backend requires. */
function PercentField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: number;
  onChange: (fraction: number) => void;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-foreground">{label}</span>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="number"
          min={0}
          max={100}
          step={0.5}
          // Stored as a fraction; shown as whole percent. See quote.ts.
          value={fractionToPercentInput(value)}
          onChange={(e) => {
            const pct = Number(e.target.value);
            if (Number.isNaN(pct)) return;
            onChange(percentInputToFraction(pct));
          }}
          className="w-28 h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
        />
        <span className="text-sm text-muted-foreground">%</span>
      </div>
      {hint && <span className="text-[12px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

function ResultRow({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: number | undefined;
  emphasis?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between py-2 ${
        emphasis ? 'border-t border-border mt-1 pt-3' : ''
      }`}
    >
      <span className={`text-sm ${emphasis ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
        {label}
      </span>
      <span className={`text-sm tabular-nums ${emphasis ? 'font-semibold text-foreground' : 'text-foreground'}`}>
        {value === undefined ? '—' : formatPrice(value)}
      </span>
    </div>
  );
}

export function CalculatorPanel({ canManage, onSelectionSaved }: CalculatorPanelProps) {
  const { data: features = [], isLoading } = useSalesFeatures({ isActive: true });
  // The configured standard commission. Quotes must start from THIS, not a
  // hardcoded rate — otherwise every commission quote silently prices at the
  // literal default whenever Finance changes the standard in the config tab.
  const { data: assumptions } = useSalesAssumptions();
  const preview = useCalculatorPreview();
  const persist = usePersistSelection();

  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [commercialModel, setCommercialModel] = useState<SalesCommercialModel>(
    SalesCommercialModel.ONE_TIME,
  );
  const [chosenTier, setChosenTier] = useState<SalesPriceTier>(SalesPriceTier.IDEAL);
  // `null` until either the assumptions load or the user types — see buildPayload:
  // while null we omit the field entirely so the backend applies its own standard.
  const [commissionPercentage, setCommissionPercentage] = useState<number | null>(null);
  const [commissionEdited, setCommissionEdited] = useState(false);
  const [prospectName, setProspectName] = useState('');
  const [result, setResult] = useState<SalesCalculatorResult | null>(null);

  // Seed from the configured standard once it arrives, unless the user already
  // overrode it for a negotiated deal.
  useEffect(() => {
    if (commissionEdited) return;
    if (assumptions?.standardCommissionPercentage === undefined) return;
    setCommissionPercentage(assumptions.standardCommissionPercentage);
  }, [assumptions?.standardCommissionPercentage, commissionEdited]);

  const coreFeatures = useMemo(
    () => features.filter((f) => f.category === 'CORE'),
    [features],
  );
  const addOnFeatures = useMemo(
    () => features.filter((f) => f.category === 'ADD_ON'),
    [features],
  );

  const isCommission = commercialModel === SalesCommercialModel.COMMISSION_ON_SALES;

  // Only sends a rate for the commission model, and only once we actually have
  // one — if the assumptions haven't loaded and the user hasn't typed, omitting
  // the field lets the backend apply its own standardCommissionPercentage
  // rather than us inventing a number. Covered by quote.test.ts.
  const buildPayload = () =>
    buildQuotePayload({
      addOnFeatureIds: selectedAddOns,
      commercialModel,
      chosenTier,
      commissionPercentage,
    });

  const handleCompute = async () => {
    const res = await preview.mutateAsync(buildPayload());
    setResult(res);
  };

  const handleSaveQuote = async () => {
    const selection = await persist.mutateAsync({
      ...buildPayload(),
      prospectName: prospectName.trim() || undefined,
    });
    setResult(selection);
    onSelectionSaved(selection);
  };

  const toggleAddOn = (id: string) =>
    setSelectedAddOns((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  if (isLoading) {
    return <div className="text-center py-16 text-muted-foreground">Loading the price book…</div>;
  }

  if (features.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
        No active features in the price book yet. Add them under the “Price book” tab before quoting.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
      {/* ---- Inputs ---- */}
      <div className="space-y-6">
        <section className="bg-card rounded-2xl border border-border p-5">
          <h2 className="text-[15px] font-semibold text-foreground">Commercial model</h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            How the prospect pays. Commission quotes price the platform fee against an agreed
            share of sales instead of a one-time build fee.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {(Object.keys(COMMERCIAL_MODEL_LABELS) as SalesCommercialModel[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setCommercialModel(m)}
                className={`px-4 h-[40px] rounded-[8px] border text-sm transition-colors ${
                  commercialModel === m
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background text-foreground hover:bg-muted'
                }`}
              >
                {COMMERCIAL_MODEL_LABELS[m]}
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className="text-[13px] font-medium text-foreground">Quoted tier</span>
              <div className="mt-1 flex gap-2">
                {(Object.keys(PRICE_TIER_LABELS) as SalesPriceTier[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setChosenTier(t)}
                    className={`px-3 h-[40px] rounded-[8px] border text-sm transition-colors ${
                      chosenTier === t
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background text-foreground hover:bg-muted'
                    }`}
                  >
                    {PRICE_TIER_LABELS[t]}
                  </button>
                ))}
              </div>
            </div>
            {isCommission && (
              <PercentField
                label="Commission on sales"
                value={commissionPercentage ?? 0}
                onChange={(fraction) => {
                  setCommissionEdited(true);
                  setCommissionPercentage(fraction);
                }}
                hint={
                  commissionEdited
                    ? 'Negotiated rate — overrides the configured standard.'
                    : 'Pre-filled from the configured standard rate (Assumptions tab).'
                }
              />
            )}
          </div>
        </section>

        <section className="bg-card rounded-2xl border border-border p-5">
          <h2 className="text-[15px] font-semibold text-foreground">Core build</h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            Always included — every quote carries the full core set. Shown for transparency.
          </p>
          <ul className="mt-3 divide-y divide-border">
            {coreFeatures.map((f) => (
              <li key={f.id} className="flex items-center justify-between py-2">
                <span className="text-sm text-foreground">{f.name}</span>
                <span className="text-sm text-muted-foreground tabular-nums">
                  {formatPrice(f.developmentCostMinor)}
                </span>
              </li>
            ))}
            {coreFeatures.length === 0 && (
              <li className="py-2 text-sm text-muted-foreground">No core features configured.</li>
            )}
          </ul>
        </section>

        <section className="bg-card rounded-2xl border border-border p-5">
          <h2 className="text-[15px] font-semibold text-foreground">Add-ons</h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            Tick what the prospect is buying on top of the core build.
          </p>
          <ul className="mt-3 divide-y divide-border">
            {addOnFeatures.map((f) => (
              <li key={f.id} className="flex items-center justify-between py-2 gap-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedAddOns.includes(f.id)}
                    onChange={() => toggleAddOn(f.id)}
                    className="h-4 w-4 rounded border-border"
                  />
                  <span className="text-sm text-foreground">{f.name}</span>
                </label>
                <span className="text-sm text-muted-foreground tabular-nums shrink-0">
                  {formatPrice(f.developmentCostMinor)}
                </span>
              </li>
            ))}
            {addOnFeatures.length === 0 && (
              <li className="py-2 text-sm text-muted-foreground">No add-ons configured.</li>
            )}
          </ul>
        </section>
      </div>

      {/* ---- Result ---- */}
      <div className="lg:sticky lg:top-6 h-fit space-y-4">
        <section className="bg-card rounded-2xl border border-border p-5">
          <h2 className="text-[15px] font-semibold text-foreground">Quote</h2>

          <div className="mt-3">
            <ResultRow label="Core (minimum)" value={result?.coreSubtotalMinMinor} />
            <ResultRow label="Core (ideal)" value={result?.coreSubtotalIdealMinor} />
            <ResultRow label="Add-ons (minimum)" value={result?.addOnSubtotalMinMinor} />
            <ResultRow label="Add-ons (ideal)" value={result?.addOnSubtotalIdealMinor} />
            <ResultRow label="Annual platform fee" value={result?.totalPlatformFeeMinor} />
            <ResultRow
              label="Total payable (minimum)"
              value={result?.totalPayableMinMinor}
              emphasis
            />
            <ResultRow label="Total payable (ideal)" value={result?.totalPayableIdealMinor} emphasis />
          </div>

          {!result && (
            <p className="mt-3 text-[12px] text-muted-foreground">
              Nothing computed yet — pricing is calculated server-side from the current
              assumptions.
            </p>
          )}

          <Button
            className="w-full mt-4 h-[40px]"
            onClick={handleCompute}
            loading={preview.isPending}
            // Button only blocks clicks via `disabled`; `loading` is cosmetic.
            disabled={preview.isPending}
          >
            Compute pricing
          </Button>
        </section>

        {canManage && (
          <section className="bg-card rounded-2xl border border-border p-5">
            <h2 className="text-[15px] font-semibold text-foreground">Save as a quote</h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              Freezes these numbers. An invoice can only be raised from a saved quote.
            </p>
            <label className="block mt-3">
              <span className="text-[13px] font-medium text-foreground">Prospect name</span>
              <input
                value={prospectName}
                onChange={(e) => setProspectName(e.target.value)}
                placeholder="e.g. Belle &amp; Bloom"
                className="mt-1 w-full h-[40px] rounded-[8px] border border-border bg-background px-3 text-sm"
              />
            </label>
            <Button
              variant="outline"
              className="w-full mt-3 h-[40px]"
              onClick={handleSaveQuote}
              loading={persist.isPending}
              disabled={persist.isPending}
            >
              Save quote
            </Button>
          </section>
        )}
      </div>
    </div>
  );
}
